/**
 * Products grid stock edit: which live system a card's stock number talks to,
 * and how a successful write updates the row (docs/memory/products.md 2026-10-14).
 *
 *   Phaser → Lightspeed. The Lightspeed level is what Shopify shows as
 *            AVAILABLE (on_hand − committed): the Shopify→Lightspeed connector
 *            books every web order as a Lightspeed sale at ORDER time, so
 *            open-order units are already gone from the level.
 *   CCC    → Qoblex Total Stock == Shopify on_hand. Qoblex only deducts at
 *            fulfilment, so the write returns the allocated figure too.
 *
 * The card's strip reads stockOnHand (big number), stockCommitted ("N
 * allocated") and stockAvailable ("N to sell"); all three must move together
 * or the card sits on overnight-cache values after an edit (Rhys 2026-10-12).
 */

export const STOCK_SYSTEM_LIGHTSPEED = 'lightspeed';
export const STOCK_SYSTEM_QOBLEX = 'qoblex';

export function isPhaserProduct(product) {
  const store = String(product?.store || '').toLowerCase();
  const name = String(product?.storeDisplayName || '').toLowerCase();
  return store.includes('phaser') || name.includes('phaser');
}

export function isCccProduct(product) {
  return product?.store === 'central_coast_coffee'
    || product?.store === 'central-coast-coffee.myshopify.com'
    || product?.storeDisplayName === 'Central Coast Coffee';
}

/** 'lightspeed' (Phaser) | 'qoblex' (CCC) | null (no live stock system for this store). */
export function stockSystemFor(product) {
  if (isPhaserProduct(product)) return STOCK_SYSTEM_LIGHTSPEED;
  if (isCccProduct(product)) return STOCK_SYSTEM_QOBLEX;
  return null;
}

function finiteOr(value, fallback) {
  if (value === null || value === undefined || value === '') return fallback; // Number(null) is 0
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Lightspeed's immediate read-back can still show the OLD level for a few
 * seconds after the adjustment posts (live 2026-10-14: 5→6 read back 5, a
 * fresh read moments later showed 6). A non-noop write WAS applied, so the
 * level to show is the target; `after` is only the read-back.
 */
export function lightspeedLevelAfterWrite({ after, target, noop } = {}) {
  const readBack = finiteOr(after, null);
  const wanted = finiteOr(target, null);
  if (noop) return readBack ?? wanted;
  return wanted ?? readBack;
}

/** True when the write applied but Lightspeed's read-back has not caught up yet. */
export function lightspeedReadBackLags({ after, target, noop } = {}) {
  if (noop) return false;
  const wanted = finiteOr(target, null);
  if (wanted === null) return false;
  return finiteOr(after, null) !== wanted;
}

/**
 * The grid row after a successful write. `after` is the level the system
 * read back (Lightspeed: available; Qoblex: on hand). `allocated` is Qoblex's
 * allocation when the write returned one. Unknown `after` leaves the row alone.
 */
export function applyStockEditToRow(row, { system, after, allocated } = {}) {
  const level = finiteOr(after, null);
  if (level === null) return row;
  const rowCommitted = finiteOr(row?.stockCommitted, 0);
  if (system === STOCK_SYSTEM_LIGHTSPEED) {
    // Lightspeed holds available; on hand = available + units still allocated.
    const onHand = level + rowCommitted;
    return { ...row, stockOnHand: onHand, stock: onHand, stockCommitted: rowCommitted, stockAvailable: level };
  }
  const committed = finiteOr(allocated, rowCommitted);
  return { ...row, stockOnHand: level, stock: level, stockCommitted: committed, stockAvailable: level - committed };
}
